export type DirectorStep = "output" | "subtype" | "asset" | "description" | "confirm" | "purpose" | "model" | "quality" | "chat";

export type DirectorHelpContext = {
  mode: "landing" | "suite";
  step: DirectorStep;
  outputTypeLabel?: string | null;
  subtypeLabel?: string | null;
  assetLabel?: string | null;
  assetOptional?: boolean;
  needsVideoSpecs?: { duration: boolean; aspect: boolean } | null;
};

export function isHelpLikeMessage(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (!t) return false;
  if (t.includes("?")) return true;
  return /(help|confus|what\s+is|what\s+does|which|how|why|don['’]?t\s+know|idk|lost|stuck|recommend|suggest|options|explain|restart|start\s+over|reset)/.test(t);
}

export function buildOffScriptReply(ctx: DirectorHelpContext): string {
  const base = ctx.mode === "suite" ? "Director's Suite" : "Director";
  const restartLine = "If you want to restart, say “restart” and I’ll reset the flow.";
  switch (ctx.step) {
    case "purpose":
      return [
        `You’re at the start of the ${base} flow.`,
        "Pick a tool from the list on the left. If you’re unsure, tell me what you want to make and I’ll recommend the best tool.",
        restartLine,
      ].join("\n");
    case "output":
      // Video is the only output type left (corrido music video or TikTok
      // story). Naming the removed A2E-only types would send people looking
      // for buttons that are gone.
      return [
        "We’re at the first step. Choose Video to make a corrido music video or a TikTok story.",
        "If you’re unsure, tell me what you want to create and I’ll guide you.",
        restartLine,
      ].join("\n");
    case "subtype":
      return [
        `Pick the specific ${ctx.outputTypeLabel ? `${ctx.outputTypeLabel} ` : ""}tool you want to use.`,
        "If you’re not sure which one fits, describe your goal and I’ll recommend the best option.",
        restartLine,
      ].join("\n");
    case "asset":
      return [
        `Next I need ${ctx.assetLabel ? `your ${ctx.assetLabel.toLowerCase()}` : "a required file"}.`,
        ctx.assetOptional ? "This one is optional — you can skip it." : "Upload a file or choose one from your gallery to continue.",
        restartLine,
      ].join("\n");
    case "description":
      return [
        "Now I need your brief. Describe the subject, style, and mood in plain language.",
        "This text becomes the creative prompt for the job.",
        restartLine,
      ].join("\n");
    case "model":
      return [
        "Pick the model you want to use. Each model has different strengths.",
        "If you want a recommendation, tell me your goal and I’ll suggest the best one.",
        restartLine,
      ].join("\n");
    case "quality":
      return [
        "Choose a quality level to balance speed and detail.",
        "Fast is quickest, Standard is best for most jobs, High is highest quality.",
        restartLine,
      ].join("\n");
    case "confirm": {
      const needs = ctx.needsVideoSpecs;
      const specLine = needs && (needs.duration || needs.aspect)
        ? "Before launching, pick a duration and aspect ratio."
        : "Review the brief and launch when you’re ready.";
      return [
        "You’re at the review step.",
        specLine,
        restartLine,
      ].join("\n");
    }
    case "chat":
    default:
      return [
        "I’m here to help. Ask anything about the tools or your idea.",
        restartLine,
      ].join("\n");
  }
}
